import React from 'react'
import { useParams } from 'react-router-dom'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { useStableHandler } from '@sketch/utils'
import { useAnalytics, RouteParams, routes } from '@sketch/modules-common'
import {
  useResponsiveDropdown,
  useBreakpoint,
  SFSymbolLabel,
  TruncateWithTooltip,
  InfiniteList,
  handleFetchMore,
} from '@sketch/components'
import {
  SidebarItem,
  UnreadCommentIcon,
} from '../../../components/PageList/PageList.styles'
import { Trigger, CurrentPage, Chevrons } from './PagesFloatingPanel.styles'
import {
  useGetPreviewPagesQuery,
  GetPreviewPagesQuery,
} from '@sketch/gql-types'

const DROPDOWN_STYLES = {
  minWidth: 160,
  overflowY: 'auto',
  maxHeight: '80vh',
} as const

type Page = GetPreviewPagesQuery['preview']['pages']['entries'][0]

interface PageListProps {
  previewID: string
  pages: Page[]
  after: string | null
  fetchMore: any
}

const PageList = ({ pages, after, fetchMore, previewID }: PageListProps) => {
  const { trackEvent } = useAnalytics()

  const onPageClick = useStableHandler((pageUUID: string | null) => {
    /**
     * Do not track the `DOCUMENT - open page` event if:
     *  - user clicked on the "All Pages" link
     */
    const isAllPagesLink = pageUUID === null
    if (isAllPagesLink) {
      return
    }

    trackEvent('DOCUMENT - open page', {
      dropdown: true,
    })
  })

  return (
    <InfiniteList
      wrappingComponent={React.Fragment}
      onLoadMore={handleFetchMore(
        fetchMore,
        ['data', 'preview', 'pages', 'entries'],
        {
          dataIdFromObject,
          after,
        }
      )}
    >
      {pages.map(({ identifier, name, uuid, hasUnreadComments }, index) => {
        let toRoute = ''

        if (index === 0) {
          toRoute = routes.PREVIEW_VIEW.create({ previewID })
        } else {
          toRoute = routes.PREVIEW_PAGE_VIEW.create({
            previewID,
            pageUUID: uuid || '',
          })
        }

        return (
          <SidebarItem
            key={identifier}
            data-testid="page-canvas-sidebar-item"
            to={toRoute}
            onClick={() => onPageClick(uuid)}
            exact
          >
            <TruncateWithTooltip>
              {name && <SFSymbolLabel text={name} />}
            </TruncateWithTooltip>
            {hasUnreadComments && <UnreadCommentIcon />}
          </SidebarItem>
        )
      })}
    </InfiniteList>
  )
}

export default PageList

interface PagesFloatingPanelforPreviewProps {
  previewIdentifier: string
}

export const PagesFloatingPanelforPreview: React.FC<
  PagesFloatingPanelforPreviewProps
> = ({ previewIdentifier }) => {
  const isMobile = !useBreakpoint('sm')
  // selected page
  const { pageUUID } = useParams<RouteParams<'PREVIEW_PAGE_VIEW'>>()
  const { data, loading, error, fetchMore } = useGetPreviewPagesQuery({
    variables: {
      previewIdentifier,
    },
  })

  const pages = data?.preview.pages.entries || []
  const pagesCount = data?.preview.pages.meta.totalCount

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: PageList,
    dropdownProps: {
      pages,
      after: data?.preview.pages.meta.after || null,
      fetchMore,
      previewID: previewIdentifier,
    },
    usePortal: true,
    placement: isMobile ? 'top-start' : 'bottom-start',
    dropdownStyle: DROPDOWN_STYLES,
  })

  if (loading) {
    return <>loading</>
  }

  if (error || !data) {
    return <>error</>
  }

  const selectedPage = pages.find(page => page.uuid === pageUUID) || pages[0]
  const onlyOnePage = !!(pagesCount && pagesCount === 1) && !!selectedPage

  return (
    <>
      <Trigger
        {...buttonProps}
        aria-label="Canvas pages list"
        data-testid="pages-selector-trigger-preview"
        disabled={onlyOnePage}
      >
        <CurrentPage>
          <>
            <em>Page:</em>{' '}
            {selectedPage
              ? selectedPage.name && <SFSymbolLabel text={selectedPage.name} />
              : 'Select page...'}
          </>
        </CurrentPage>
        {!onlyOnePage && <Chevrons />}
      </Trigger>
      {!onlyOnePage && content}
    </>
  )
}
